import { RadioGroup } from '@headlessui/react';
import clsx from 'clsx';
import { forwardRef } from 'react';

interface RadioGridProps {
  label: string;
  name: string;
  defaultValue?: string;
  onChange: (value: string) => void;
  options: { value: string; label: string; thumbnail: string }[];
  maxHeight?: number;
}

export const RadioGrid = forwardRef<HTMLDivElement, RadioGridProps>(
  ({ label, name, defaultValue, onChange, options, maxHeight }, ref) => {
    const listClassName = clsx(
      'grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:gap-x-8 mt-1',
      maxHeight &&
        'overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent hover:scrollbar-thumb-gray-500 dark:hover:scrollbar-thumb-gray-500 overscroll-contain p-1'
    );

    return (
      <RadioGroup
        name={name}
        defaultValue={defaultValue}
        onChange={onChange}
        ref={ref}
      >
        <RadioGroup.Label
          as="p"
          className="text-sm font-medium text-gray-900 dark:text-gray-100"
        >
          {label}
        </RadioGroup.Label>
        <ul
          role="list"
          className={listClassName}
          style={{ maxHeight: maxHeight ? `${maxHeight}px` : 'none' }}
        >
          {options.map((option) => (
            <li key={option.value} className="relative">
              <RadioGroup.Option
                className="group outline-none"
                value={option.value}
              >
                {({ active, checked }) => (
                  <>
                    <div className="overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800 group-focus:ring-2 group-focus:ring-indigo-500 dark:group-focus:ring-indigo-600 group-focus:ring-offset-2 group-focus:ring-offset-gray-100 dark:group-focus:ring-offset-gray-800">
                      <img
                        alt=""
                        src={option.thumbnail}
                        className="pointer-events-none aspect-[10/7] object-cover group-hover:opacity-75"
                      />
                    </div>
                    <RadioGroup.Label
                      as="p"
                      className="pointer-events-none mt-2 block truncate text-sm font-medium text-gray-900 dark:text-gray-100"
                    >
                      {option.label}
                    </RadioGroup.Label>
                    {checked && (
                      <CheckIcon
                        className="h-6 w-6 absolute top-1 right-1"
                        aria-hidden="true"
                        aria-label="Selected"
                      />
                    )}
                  </>
                )}
              </RadioGroup.Option>
            </li>
          ))}
        </ul>
      </RadioGroup>
    );
  }
);

function CheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx={12} cy={12} r={12} fill="#000" opacity="0.2" />
      <path
        d="M7 13l3 3 7-7"
        stroke="#fff"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
